import { translate, useLocale } from "../localization";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import API from "../services/API";
import {
  clearThumbnailCache,
  getThumbnail as getContentThumbnail,
} from "../services/Content";

export const ContentContext = createContext(null);

const ELEMENTS_STALE_MS = 30 * 1000;
const ELEMENT_STATUS_POLL_DELAYS_MS = [5000, 5000, 10000, 15000, 30000];
const BUILDING_ELEMENT_STATUS = "BUILDING";
const ELEMENT_RUNTIME_QUERY = "version=6000&platform=android&runtimePlatform=windows";
const MY_ELEMENTS_ENDPOINT = `elements?${ELEMENT_RUNTIME_QUERY}&mine=true`;

const isTerminalElementStatus = (statusType = "") => /COMPLETE|FAILED/i.test(statusType);
const shouldPollElementStatus = (statusType = "") =>
  Boolean(statusType) && !isTerminalElementStatus(statusType);
const isNotFoundError = (error) => Number(error?.response?.status) === 404;

const mergeItemById = (list, item, { prepend = false } = {}) => {
  if (!item?.id) return Array.isArray(list) ? list : [];

  const safeList = Array.isArray(list) ? list : [];
  const existingIndex = safeList.findIndex((entry) => entry?.id === item.id);

  if (existingIndex === -1) {
    return prepend ? [item, ...safeList] : [...safeList, item];
  }

  return safeList.map((entry, index) =>
    index === existingIndex ? { ...entry, ...item } : entry
  );
};

const updateItemById = (list, itemId, updater) => {
  if (!itemId || !Array.isArray(list)) return Array.isArray(list) ? list : [];

  return list.map((item) => {
    if (item?.id !== itemId) return item;

    const nextPatch = typeof updater === "function" ? updater(item) : updater;
    return nextPatch ? { ...item, ...nextPatch } : item;
  });
};

const reconcileFetchedElements = (serverElements, currentElements) => {
  const safeServerElements = Array.isArray(serverElements) ? serverElements : [];
  const safeCurrentElements = Array.isArray(currentElements) ? currentElements : [];
  const pendingLocalElements = safeCurrentElements.filter((localElement) => {
    const localStatusType = localElement?.status?.type || "";

    return (
      localElement?.id &&
      localStatusType &&
      !isTerminalElementStatus(localStatusType) &&
      !safeServerElements.some((serverElement) => serverElement?.id === localElement.id)
    );
  });

  return [...pendingLocalElements, ...safeServerElements];
};

const getFirstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const getFirstObject = (...values) => {
  for (const value of values) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value;
    }
  }

  return null;
};

const getBuildTrackingRecord = (...records) => {
  const candidates = [];

  records.forEach((record) => {
    if (!record || typeof record !== "object" || Array.isArray(record)) {
      return;
    }

    candidates.push(
      record.buildTracking,
      record.buildInfo,
      record.metadata?.buildTracking,
      record.metadata?.buildInfo
    );
  });

  return getFirstObject(...candidates);
};

const extractElementList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const extractElementSource = (payload) => {
  if (payload?.data && !Array.isArray(payload.data)) {
    return payload.data;
  }

  return payload;
};

const extractStatusRecord = (payload) => {
  if (!payload) return null;

  if (typeof payload === "string") {
    return {
      type: payload,
      timestamp: new Date().toISOString(),
      message: "",
    };
  }

  if (payload.data) {
    const dataStatus = extractStatusRecord(payload.data);
    if (dataStatus) return dataStatus;
  }

  const nestedStatus =
    payload.status && typeof payload.status === "object"
      ? extractStatusRecord(payload.status)
      : null;
  const metadataStatus =
    payload.metadata?.status && typeof payload.metadata.status === "object"
      ? extractStatusRecord(payload.metadata.status)
      : null;
  const inheritedStatus = nestedStatus || metadataStatus;

  if (
    inheritedStatus &&
    !payload.type &&
    !payload.state &&
    !payload.statusType &&
    typeof payload.status !== "string"
  ) {
    return {
      ...inheritedStatus,
      timestamp:
        payload.timestamp ||
        payload.updatedAt ||
        payload.dateModified ||
        inheritedStatus.timestamp,
      message:
        getFirstNonEmptyString(
          payload.message,
          payload.statusMessage,
          payload.detail,
          payload.details,
          payload.reason,
          payload.error
        ) || inheritedStatus.message,
      stage: getFirstNonEmptyString(payload.stage, inheritedStatus.stage),
      buildTracking: getBuildTrackingRecord(payload, inheritedStatus),
    };
  }

  const type =
    (typeof payload.type === "string" && payload.type) ||
    (typeof payload.state === "string" && payload.state) ||
    (typeof payload.statusType === "string" && payload.statusType) ||
    (typeof payload.status === "string" && payload.status) ||
    nestedStatus?.type ||
    metadataStatus?.type ||
    "";

  if (!type) return null;

  const buildTracking = getBuildTrackingRecord(
    payload,
    nestedStatus,
    metadataStatus
  );
  const stage = getFirstNonEmptyString(
    payload.stage,
    nestedStatus?.stage,
    metadataStatus?.stage
  );

  return {
    type,
    timestamp:
      payload.timestamp ||
      payload.updatedAt ||
      payload.dateModified ||
      nestedStatus?.timestamp ||
      metadataStatus?.timestamp ||
      new Date().toISOString(),
    message: getFirstNonEmptyString(
      payload.message,
      payload.statusMessage,
      payload.detail,
      payload.details,
      payload.reason,
      payload.error,
      nestedStatus?.message,
      metadataStatus?.message
    ),
    ...(stage ? { stage } : {}),
    ...(buildTracking ? { buildTracking } : {}),
  };
};

const normalizeElementSummary = (payload) => {
  const source = extractElementSource(payload);
  if (!source || typeof source !== "object") return null;

  const id = getFirstNonEmptyString(source.id, source.dataObjectID, source.metadata?.id);
  if (!id) return null;

  return {
    id,
    name: getFirstNonEmptyString(
      source.name,
      source.displayName,
      source.labelData?.displayName
    ),
    description: getFirstNonEmptyString(
      source.description,
      source.labelData?.description
    ),
    dateModified: getFirstNonEmptyString(
      source.dateModified,
      source.metadata?.dateModified
    ),
    lastModified: getFirstNonEmptyString(source.lastModified),
    updatedAt: getFirstNonEmptyString(source.updatedAt),
    status: extractStatusRecord(source.status || source.currentStatus || source),
  };
};

/**
 * Provider component that holds and manages content items (systems, elements,
 * environments, collections) in state.
 */
export const ContentProvider = ({ children }) => {
  const locale = useLocale();
  const [contentState, setContentState] = useState({});
  const [mySystems, setMySystems] = useState([]);
  const [myElements, setMyElements] = useState([]);
  const [myEnvironments, setMyEnvironments] = useState([]);
  const [myCollections, setMyCollections] = useState([]);
  const [allEnvironments, setAllEnvironments] = useState([]);

  const [environmentsLoading, setEnvironmentsLoading] = useState(true);
  const [systemsLoading, setSystemsLoading] = useState(true);
  const [elementsLoading, setElementsLoading] = useState(true);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [collectionsLoaded, setCollectionsLoaded] = useState(false);

  const itemRequestCacheRef = useRef(new Map());
  const elementsRequestRef = useRef(null);
  const lastElementsFetchAtRef = useRef(0);
  const elementStatusTimersRef = useRef(new Map());
  const elementStatusEndpointMissingRef = useRef(new Set());
  const myElementsRef = useRef(myElements);

  useEffect(() => {
    myElementsRef.current = myElements;
  }, [myElements]);

  const makeKey = useCallback((contentType, itemId) => `${contentType}-${itemId}`, []);

  const invalidateContentItem = useCallback(
    (contentType, itemId) => {
      if (!contentType || !itemId) return;

      const key = makeKey(contentType, itemId);
      itemRequestCacheRef.current.delete(key);

      setContentState((previousContentState) => {
        if (!(key in previousContentState)) {
          return previousContentState;
        }

        const nextContentState = { ...previousContentState };
        delete nextContentState[key];
        return nextContentState;
      });
    },
    [makeKey]
  );

  const environmentLookup = useMemo(() => {
    const lookup = {};

    allEnvironments.forEach((environment) => {
      if (!environment?.id) return;

      lookup[environment.id] = environment;
      lookup[makeKey("environments", environment.id)] = environment;
    });

    return lookup;
  }, [allEnvironments, makeKey, locale]);

  const getItem = useCallback(
    async (contentType, itemId, requireAuth = true, options = {}) => {
      const key = makeKey(contentType, itemId);
      const forceRefresh = options?.forceRefresh === true;

      if (!forceRefresh && contentState[key]) {
        return contentState[key];
      }

      if (!forceRefresh && contentType === "environments" && environmentLookup[itemId]) {
        return environmentLookup[itemId];
      }

      if (!forceRefresh && itemRequestCacheRef.current.has(key)) {
        return itemRequestCacheRef.current.get(key);
      }

      const request = API.get(`${contentType}/${itemId}`, {
        requiresAuth: requireAuth,
      })
        .then((response) => {
          const data = response.data;

          if (itemRequestCacheRef.current.get(key) === request) {
            setContentState((previousContentState) => ({
              ...previousContentState,
              [key]: data,
            }));
          }

          return data;
        })
        .catch((error) => {
          console.error(`Error fetching ${contentType}/${itemId}:`, error);
          return null;
        })
        .finally(() => {
          if (itemRequestCacheRef.current.get(key) === request) {
            itemRequestCacheRef.current.delete(key);
          }
        });

      itemRequestCacheRef.current.set(key, request);
      return request;
    },
    [contentState, environmentLookup, makeKey]
  );

  const getEnvironmentSync = useCallback(
    (environmentId) => {
      if (!environmentId) return null;
      return environmentLookup[environmentId] || null;
    },
    [environmentLookup]
  );

  const getEnvironmentName = useCallback(
    (environmentId) => {
      if (!environmentId) return translate("Unknown");
      const environment = environmentLookup[environmentId];
      return environment?.name || environmentId;
    },
    [environmentLookup, locale]
  );

  const getThumbnail = useCallback(
    (id, options) => getContentThumbnail(id, options),
    []
  );

  const fetchSystems = async () => {
    setSystemsLoading(true);
    try {
      const response = await API.get("systems?mine=true", { requiresAuth: true });
      setMySystems(response.data || []);
    } catch (error) {
      console.error("Error fetching systems:", error);
    } finally {
      setSystemsLoading(false);
    }
  };

  const fetchElements = useCallback(async (force = false) => {
    if (elementsRequestRef.current) {
      return elementsRequestRef.current;
    }

    if (
      !force &&
      lastElementsFetchAtRef.current &&
      Date.now() - lastElementsFetchAtRef.current < ELEMENTS_STALE_MS
    ) {
      return myElementsRef.current;
    }

    const shouldShowLoading = !lastElementsFetchAtRef.current;
    if (shouldShowLoading) {
      setElementsLoading(true);
    }

    const request = API.get(
      MY_ELEMENTS_ENDPOINT,
      { requiresAuth: true }
    )
      .then((response) => {
        const nextElements = extractElementList(response?.data)
          .map((element) => normalizeElementSummary(element))
          .filter(Boolean);
        lastElementsFetchAtRef.current = Date.now();
        setMyElements((previousElements) =>
          reconcileFetchedElements(nextElements, previousElements)
        );
        return nextElements;
      })
      .catch((error) => {
        console.error("Error fetching elements:", error);
        return [];
      })
      .finally(() => {
        setElementsLoading(false);
        elementsRequestRef.current = null;
      });

    elementsRequestRef.current = request;
    return request;
  }, []);

  const fetchEnvironments = async () => {
    setEnvironmentsLoading(true);
    try {
      const allResponse = await API.get("environments", {
        requiresAuth: true,
      });
      const nextAllEnvironments = allResponse.data || [];
      setAllEnvironments(nextAllEnvironments);

      if (Array.isArray(nextAllEnvironments) && nextAllEnvironments.length > 0) {
        const nextContentState = {};
        nextAllEnvironments.forEach((environment) => {
          if (environment?.id) {
            nextContentState[makeKey("environments", environment.id)] = environment;
          }
        });

        setContentState((previousContentState) => ({
          ...previousContentState,
          ...nextContentState,
        }));
      }

      const myResponse = await API.get("environments?mine=true", {
        requiresAuth: true,
      });
      setMyEnvironments(myResponse.data || []);
    } catch (error) {
      console.error("Error fetching environments:", error);
    } finally {
      setEnvironmentsLoading(false);
    }
  };

  const fetchCollections = useCallback(
    async (force = false) => {
      if (!force && collectionsLoaded) {
        return myCollections;
      }

      setCollectionsLoading(true);
      try {
        const response = await API.get("collections?mine=true", {
          requiresAuth: true,
        });

        const collections = Array.isArray(response.data) ? response.data : [];
        setMyCollections(collections);
        setCollectionsLoaded(true);

        if (collections.length > 0) {
          const nextContentState = {};
          collections.forEach((collection) => {
            if (collection?.id) {
              nextContentState[makeKey("collections", collection.id)] = collection;
            }
          });

          setContentState((previousContentState) => ({
            ...previousContentState,
            ...nextContentState,
          }));
        }

        return collections;
      } catch (error) {
        console.error("Error fetching collections:", error);
        return [];
      } finally {
        setCollectionsLoading(false);
      }
    },
    [collectionsLoaded, makeKey, myCollections]
  );

  const clearElementStatusPoll = useCallback((elementId) => {
    const existingTimer = elementStatusTimersRef.current.get(elementId);
    if (!existingTimer) return;

    clearTimeout(existingTimer);
    elementStatusTimersRef.current.delete(elementId);
  }, []);

  const upsertMyElement = useCallback(
    (element, { prepend = false } = {}) => {
      if (!element?.id) return;

      invalidateContentItem("elements", element.id);
      setMyElements((previousElements) =>
        mergeItemById(previousElements, element, { prepend })
      );
    },
    [invalidateContentItem]
  );

  const patchMyElement = useCallback(
    (elementId, patch) => {
      if (!elementId) return;

      invalidateContentItem("elements", elementId);
      setMyElements((previousElements) =>
        updateItemById(previousElements, elementId, patch)
      );
    },
    [invalidateContentItem]
  );

  const removeMyElement = useCallback(
    (elementId) => {
      if (!elementId) return;

      clearElementStatusPoll(elementId);
      elementStatusEndpointMissingRef.current.delete(elementId);
      invalidateContentItem("elements", elementId);
      setMyElements((previousElements) =>
        Array.isArray(previousElements)
          ? previousElements.filter((element) => element?.id !== elementId)
          : []
      );
    },
    [clearElementStatusPoll, invalidateContentItem]
  );

  const fetchElementStatus = useCallback(async (elementId) => {
    const detailEndpoint = `elements/${elementId}?${ELEMENT_RUNTIME_QUERY}`;
    const statusEndpoint = `elements/${elementId}/status?${ELEMENT_RUNTIME_QUERY}`;

    if (!elementStatusEndpointMissingRef.current.has(elementId)) {
      try {
        const statusResponse = await API.get(statusEndpoint, {
          requiresAuth: true,
        });

        const directStatus = extractStatusRecord(statusResponse?.data);
        if (directStatus) {
          return directStatus;
        }
      } catch (error) {
        if (!isNotFoundError(error)) {
          throw error;
        }
        elementStatusEndpointMissingRef.current.add(elementId);
      }
    }

    try {
      const detailResponse = await API.get(detailEndpoint, {
        requiresAuth: true,
      });

      return extractStatusRecord(detailResponse?.data);
    } catch (error) {
      if (isNotFoundError(error)) {
        return null;
      }
      throw error;
    }
  }, []);

  const scheduleElementStatusPoll = useCallback(
    (elementId, attempt = 0, immediate = false) => {
      if (!elementId) return;

      clearElementStatusPoll(elementId);

      const delayMs = immediate
        ? 0
        : ELEMENT_STATUS_POLL_DELAYS_MS[
            Math.min(attempt, ELEMENT_STATUS_POLL_DELAYS_MS.length - 1)
          ];

      const timerId = window.setTimeout(async () => {
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
          scheduleElementStatusPoll(elementId, attempt, false);
          return;
        }

        try {
          const nextStatus = await fetchElementStatus(elementId);

          if (!nextStatus) {
            clearElementStatusPoll(elementId);
            return;
          }

          patchMyElement(elementId, { status: nextStatus });

          if (isTerminalElementStatus(nextStatus.type)) {
            clearElementStatusPoll(elementId);
            return;
          }
        } catch (error) {
          if (isNotFoundError(error)) {
            clearElementStatusPoll(elementId);
            return;
          }
          console.warn(`Error polling status for element ${elementId}:`, error);
        }

        scheduleElementStatusPoll(elementId, attempt + 1, false);
      }, delayMs);

      elementStatusTimersRef.current.set(elementId, timerId);
    },
    [clearElementStatusPoll, fetchElementStatus, patchMyElement]
  );

  const ensureElementStatusPoll = useCallback(
    (elementId, { attempt = 0, immediate = false, restart = false } = {}) => {
      if (!elementId) return;

      const hasExistingTimer = elementStatusTimersRef.current.has(elementId);
      if (hasExistingTimer && !restart) {
        return;
      }

      scheduleElementStatusPoll(elementId, attempt, immediate);
    },
    [scheduleElementStatusPoll]
  );

  const trackElementBuild = useCallback(
    (elementId, elementSummary = null) => {
      if (!elementId) return;

      const pendingStatus = {
        type: BUILDING_ELEMENT_STATUS,
        timestamp: new Date().toISOString(),
      };

      if (elementSummary) {
        upsertMyElement(
          {
            ...elementSummary,
            id: elementId,
            status: pendingStatus,
          },
          { prepend: true }
        );
      } else {
        patchMyElement(elementId, { status: pendingStatus });
      }

      ensureElementStatusPoll(elementId, { attempt: 0, immediate: true, restart: true });
    },
    [ensureElementStatusPoll, patchMyElement, upsertMyElement]
  );

  useEffect(() => {
    const activeElementIds = new Set();

    myElements.forEach((element) => {
      const elementId = element?.id;
      if (!elementId) return;

      activeElementIds.add(elementId);

      if (shouldPollElementStatus(element?.status?.type || "")) {
        ensureElementStatusPoll(elementId);
      } else {
        clearElementStatusPoll(elementId);
      }
    });

    elementStatusTimersRef.current.forEach((_, elementId) => {
      if (!activeElementIds.has(elementId)) {
        clearElementStatusPoll(elementId);
      }
    });
  }, [clearElementStatusPoll, ensureElementStatusPoll, myElements]);

  useEffect(() => {
    const itemRequestCache = itemRequestCacheRef.current;
    const elementStatusTimers = elementStatusTimersRef.current;
    const missingStatusEndpoints = elementStatusEndpointMissingRef.current;

    return () => {
      clearThumbnailCache();
      itemRequestCache.clear();

      elementStatusTimers.forEach((timerId) => {
        clearTimeout(timerId);
      });
      elementStatusTimers.clear();
      missingStatusEndpoints.clear();
    };
  }, []);

  useEffect(() => {
    fetchSystems();
    fetchElements();
    fetchEnvironments();
    fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = {
    getItem,
    getThumbnail,
    invalidateContentItem,
    mySystems,
    fetchSystems,
    myElements,
    fetchElements,
    upsertMyElement,
    patchMyElement,
    removeMyElement,
    trackElementBuild,
    myEnvironments,
    allEnvironments,
    fetchEnvironments,
    myCollections,
    fetchCollections,
    getEnvironmentSync,
    getEnvironmentName,
    environmentLookup,
    environmentsLoading,
    systemsLoading,
    elementsLoading,
    collectionsLoading,
  };

  return <ContentContext.Provider value={contextValue}>{children}</ContentContext.Provider>;
};

export const useContent = () => {
  useLocale();
  return useContext(ContentContext);
};
